/**
 * FakturaRegistrering - Modal for å registrere ny faktura med fordeling
 * Kan brukes fra Dashboard eller fra SakDetaljer
 */

import React, { useState, useEffect } from 'react';
import './FakturaRegistrering.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://advokat-crm-api.stig-958.workers.dev';

export default function FakturaRegistrering({ sakId = null, onClose, onSuccess }) {
  const [steg, setSteg] = useState(1); // 1=Grunninfo, 2=Fordeling, 3=Oppsummering
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sak-valg (hvis ikke forhåndsvalgt)
  const [saker, setSaker] = useState([]);
  const [valgtSakId, setValgtSakId] = useState(sakId);

  // Grunnleggende fakturainfo
  const [fakturanummer, setFakturanummer] = useState('');
  const [fakturadato, setFakturadato] = useState(new Date().toISOString().split('T')[0]);
  const [timesats, setTimesats] = useState('');
  const [antallTimer, setAntallTimer] = useState('');
  const [beskrivelse, setBeskrivelse] = useState('');
  const [mvaFritatt, setMvaFritatt] = useState(false);

  // PDF-opplasting
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);

  // Forsikringsinfo
  const [forsikringsselskap, setForsikringsselskap] = useState([]);
  const [bkfSelskapId, setBkfSelskapId] = useState('');
  const [innboSelskapId, setInnboSelskapId] = useState('');
  const [innboPolisenummer, setInnboPolisenummer] = useState('');
  const [egenandel, setEgenandel] = useState({ betaltDirekte: false, beløp: 0 });
  const [leverandorNavn, setLeverandorNavn] = useState(''); // Fra Azure

  // Rettshjelpsvilkår fra saken
  const [rettshjelpsvilkår, setRettshjelpsvilkår] = useState(null);
  const [lasterVilkår, setLasterVilkår] = useState(false);

  // Beregnet fordeling
  const [fordeling, setFordeling] = useState(null);

  // Last saker hvis ikke forhåndsvalgt
  useEffect(() => {
    if (!sakId) {
      lastSaker();
    }
    lastForsikringsselskap();
  }, [sakId]);

  // Automatisk matching av forsikringsselskap når vi går til steg 2
  useEffect(() => {
    if (steg === 2 && leverandorNavn && forsikringsselskap.length > 0) {
      matchForsikringsselskap(leverandorNavn);
    }
  }, [steg, leverandorNavn, forsikringsselskap]);

  // Last rettshjelpsvilkår når sak velges
  useEffect(() => {
    if (valgtSakId) {
      lastRettshjelpsvilkår(valgtSakId);
    }
  }, [valgtSakId]);

  async function lastSaker() {
    try {
      const res = await fetch(`${API_URL}/api/saker`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      const data = await res.json();
      // API returnerer enten direkte array eller { saker: [...] }
      const sakerData = Array.isArray(data) ? data : (data.saker || []);
      setSaker(sakerData);
    } catch (err) {
      console.error('Kunne ikke laste saker:', err);
    }
  }

  async function lastForsikringsselskap() {
    try {
      const res = await fetch(`${API_URL}/api/forsikringsselskaper`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      const data = await res.json();
      setForsikringsselskap(data.selskaper || []);
    } catch (err) {
      console.error('Kunne ikke laste forsikringsselskap:', err);
    }
  }

  async function lastRettshjelpsvilkår(sakId) {
    try {
      setLasterVilkår(true);
      const res = await fetch(`${API_URL}/api/saker/${sakId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      const sakData = await res.json();

      // Sjekk om saken har rettshjelp registrert
      if (sakData.rettshjelp_bkf_selskap_id || sakData.rettshjelp_innbo_selskap_id) {
        setRettshjelpsvilkår({
          bkf: sakData.rettshjelp_bkf_selskap_id ? {
            selskap_navn: sakData.rettshjelp_bkf_selskap_navn,
            polisenummer: sakData.rettshjelp_bkf_polisenummer,
            timesats: sakData.rettshjelp_bkf_timesats,
            tillegg: sakData.rettshjelp_bkf_tillegg,
            maks_belop: sakData.rettshjelp_bkf_maks_belop
          } : null,
          innbo: sakData.rettshjelp_innbo_selskap_id ? {
            selskap_navn: sakData.rettshjelp_innbo_selskap_navn,
            polisenummer: sakData.rettshjelp_innbo_polisenummer,
            dekningsgrad: sakData.rettshjelp_innbo_dekningsgrad,
            maks_belop: sakData.rettshjelp_innbo_maks_belop,
            aktiveringsgebyr: sakData.rettshjelp_innbo_aktiveringsgebyr
          } : null
        });

        console.log('[RETTSHJELP] Vilkår lastet for sak:', sakId, {
          harBKF: !!sakData.rettshjelp_bkf_selskap_id,
          harInnbo: !!sakData.rettshjelp_innbo_selskap_id
        });
      } else {
        setRettshjelpsvilkår(null);
        console.log('[RETTSHJELP] Ingen rettshjelp registrert for sak:', sakId);
      }
    } catch (err) {
      console.error('Kunne ikke laste rettshjelpsvilkår:', err);
      setRettshjelpsvilkår(null);
    } finally {
      setLasterVilkår(false);
    }
  }

  function matchForsikringsselskap(leverandorNavn) {
    if (!leverandorNavn || bkfSelskapId || innboSelskapId) {
      // Hopp over hvis allerede valgt manuelt
      return;
    }

    const navnLower = leverandorNavn.toLowerCase().trim();
    console.log('[AUTO-MATCH] Søker etter forsikringsselskap:', navnLower);

    // Fuzzy matching - sjekk om leverandørnavnet inneholder selskapsnavn eller vice versa
    for (const selskap of forsikringsselskap) {
      const selskapNavnLower = (selskap.navn || '').toLowerCase().trim();

      // Eksakt match
      if (navnLower === selskapNavnLower) {
        console.log('[AUTO-MATCH] ✓ Eksakt match funnet:', selskap.navn, '(type:', selskap.type, ')');
        if (selskap.type === 'boligkjoper') {
          setBkfSelskapId(selskap.id);
        } else if (selskap.type === 'innbo') {
          setInnboSelskapId(selskap.id);
        }
        return;
      }

      // Delvis match (ett navn inneholder det andre)
      if (navnLower.includes(selskapNavnLower) || selskapNavnLower.includes(navnLower)) {
        console.log('[AUTO-MATCH] ✓ Delvis match funnet:', selskap.navn, '(type:', selskap.type, ')');
        if (selskap.type === 'boligkjoper' && !bkfSelskapId) {
          setBkfSelskapId(selskap.id);
        } else if (selskap.type === 'innbo' && !innboSelskapId) {
          setInnboSelskapId(selskap.id);
        }
        return;
      }
    }

    console.log('[AUTO-MATCH] ✗ Ingen match funnet for:', leverandorNavn);
  }

  async function handlePDFUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Kun PDF-filer er støttet');
      return;
    }

    setUploadingPDF(true);
    setError(null);

    // Konverter til base64
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const base64 = e.target.result.split(',')[1];

        console.log('[PDF] Starter analyse av', file.name);

        // Send til backend for AI-analyse
        const res = await fetch(`${API_URL}/api/fakturaer/analyser-pdf`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pdf_base64: base64,
            filename: file.name
          })
        });

        console.log('[PDF] Response status:', res.status);

        const data = await res.json();
        console.log('[PDF] Response data:', data);

        if (!data.success) {
          throw new Error(data.error || 'Kunne ikke analysere PDF');
        }

        // Fyll ut skjema automatisk
        const analyse = data.analyse;
        console.log('[PDF] Analyse resultat:', analyse);

        if (analyse.fakturanummer) setFakturanummer(analyse.fakturanummer);
        if (analyse.fakturadato) setFakturadato(analyse.fakturadato);
        if (analyse.timesats) setTimesats(analyse.timesats.toString());
        if (analyse.antall_timer) setAntallTimer(analyse.antall_timer.toString());
        if (analyse.klient_navn) setBeskrivelse(`Klient: ${analyse.klient_navn}\nProsjekt: ${analyse.prosjekt || ''}`);
        if (analyse.mva_fritatt !== undefined) setMvaFritatt(analyse.mva_fritatt);

        // Lagre leverandørnavn for automatisk matching av forsikringsselskap
        if (analyse.leverandor_navn) {
          setLeverandorNavn(analyse.leverandor_navn);
          console.log('[PDF] Leverandør funnet:', analyse.leverandor_navn);
        }

        setPdfFile(file);
        setUploadingPDF(false);
      } catch (err) {
        console.error('[PDF] Feil ved analyse:', err);
        setError(err.message);
        setUploadingPDF(false);
      }
    };

    reader.onerror = (err) => {
      console.error('[PDF] FileReader error:', err);
      setError('Kunne ikke lese PDF-filen');
      setUploadingPDF(false);
    };

    reader.readAsDataURL(file);
  }

  function beregnTotaler() {
    const ts = parseFloat(timesats) || 0;
    const timer = parseFloat(antallTimer) || 0;
    const salær_uten_mva = ts * timer;
    const mva = mvaFritatt ? 0 : salær_uten_mva * 0.25;
    const totalt = salær_uten_mva + mva;

    return {
      salær_uten_mva,
      mva,
      totalt_inkl_mva: totalt
    };
  }

  async function opprettFaktura() {
    if (!valgtSakId) {
      setError('Velg sak');
      return;
    }

    if (!fakturanummer || !timesats || !antallTimer) {
      setError('Fyll inn alle påkrevde felt');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const totaler = beregnTotaler();

      // 1. Opprett faktura
      const res = await fetch(`${API_URL}/api/fakturaer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sak_id: valgtSakId,
          fakturanummer,
          fakturadato,
          timesats: parseFloat(timesats),
          antall_timer: parseFloat(antallTimer),
          beskrivelse
        })
      });

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Kunne ikke opprette faktura');
      }

      const fakturaId = data.faktura_id;

      // 2. Beregn fordeling (hvis BKF/Innbo valgt)
      if (bkfSelskapId && innboSelskapId) {
        await beregnFordeling(fakturaId);
      }

      setSteg(3); // Gå til oppsummering

      if (onSuccess) {
        onSuccess(fakturaId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function beregnFordeling(fakturaId) {
    const res = await fetch(`/api/fakturaer/${fakturaId}/beregn-fordeling`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        bkf_selskap_id: bkfSelskapId,
        innbo_selskap_id: innboSelskapId,
        innbo_polisenummer: innboPolisenummer,
        egenandel_betalt_direkte: egenandel.betaltDirekte
      })
    });

    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Kunne ikke beregne fordeling');
    }

    setFordeling(data.fordeling);
  }

  function formaterBeløp(beløp) {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 2
    }).format(beløp || 0);
  }

  const totaler = beregnTotaler();
  const bkfSelskap = forsikringsselskap.find(s => s.id === bkfSelskapId);
  const innboSelskap = forsikringsselskap.find(s => s.id === innboSelskapId);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content faktura-registrering" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📄 Registrer faktura</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="alert alert-error">
            ⚠️ {error}
          </div>
        )}

        {/* Steg-indikator */}
        <div className="steg-indikator">
          <div className={`steg ${steg >= 1 ? 'aktiv' : ''}`}>
            <span className="steg-nummer">1</span>
            <span className="steg-tekst">Grunninfo</span>
          </div>
          <div className={`steg ${steg >= 2 ? 'aktiv' : ''}`}>
            <span className="steg-nummer">2</span>
            <span className="steg-tekst">Fordeling</span>
          </div>
          <div className={`steg ${steg >= 3 ? 'aktiv' : ''}`}>
            <span className="steg-nummer">3</span>
            <span className="steg-tekst">Ferdig</span>
          </div>
        </div>

        <div className="modal-body">
          {/* STEG 1: Grunnleggende fakturainfo */}
          {steg === 1 && (
            <div className="steg-innhold">
              <h3>Fakturaopplysninger</h3>

              {/* PDF-opplasting */}
              <div className="pdf-upload-section">
                <label className="pdf-upload-label">
                  📄 Last opp faktura (PDF) for automatisk utfylling
                </label>
                <div className="gdpr-notice" style={{
                  fontSize: '0.85em',
                  color: '#666',
                  background: '#f8f9fa',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  marginBottom: '10px',
                  borderLeft: '3px solid #ffc107'
                }}>
                  ℹ️ <strong>Personvern:</strong> PDF-en analyseres av Anthropic Claude AI (USA).
                  Data lagres hos Anthropic i maks 30 dager og brukes ikke til AI-trening.
                </div>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handlePDFUpload}
                  disabled={uploadingPDF}
                  className="pdf-upload-input"
                />
                {uploadingPDF && (
                  <div className="upload-status">
                    <div className="spinner-small"></div>
                    <span>Analyserer faktura...</span>
                  </div>
                )}
                {pdfFile && (
                  <div className="upload-success">
                    ✓ Faktura analysert: {pdfFile.name}
                  </div>
                )}
              </div>

              {!sakId && (
                <div className="form-group">
                  <label>Sak *</label>
                  <select
                    value={valgtSakId || ''}
                    onChange={(e) => setValgtSakId(e.target.value)}
                    required
                  >
                    <option value="">-- Velg sak --</option>
                    {saker.map(sak => (
                      <option key={sak.id} value={sak.id}>
                        {sak.saksnummer} - {sak.tittel}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Fakturanummer *</label>
                  <input
                    type="text"
                    value={fakturanummer}
                    onChange={(e) => setFakturanummer(e.target.value)}
                    placeholder="2026-001"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Fakturadato *</label>
                  <input
                    type="date"
                    value={fakturadato}
                    onChange={(e) => setFakturadato(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Timesats (kr) *</label>
                  <input
                    type="number"
                    value={timesats}
                    onChange={(e) => setTimesats(e.target.value)}
                    placeholder="f.eks. 1375"
                    step="0.01"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Antall timer *</label>
                  <input
                    type="number"
                    value={antallTimer}
                    onChange={(e) => setAntallTimer(e.target.value)}
                    placeholder="f.eks. 3.5"
                    step="0.1"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Beskrivelse</label>
                <textarea
                  value={beskrivelse}
                  onChange={(e) => setBeskrivelse(e.target.value)}
                  placeholder="Kort beskrivelse av arbeidet..."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={mvaFritatt}
                    onChange={(e) => setMvaFritatt(e.target.checked)}
                  />
                  <span>MVA-fritatt (f.eks. rettshjelp dekket av forsikring)</span>
                </label>
              </div>

              {/* Forhåndsvisning av totaler */}
              <div className="totaler-preview">
                <div className="total-rad">
                  <span>Salær uten mva:</span>
                  <span>{formaterBeløp(totaler.salær_uten_mva)}</span>
                </div>
                <div className="total-rad">
                  <span>MVA (25%):</span>
                  <span>{formaterBeløp(totaler.mva)}</span>
                </div>
                <div className="total-rad total-sum">
                  <span><strong>Totalt inkl. mva:</strong></span>
                  <span><strong>{formaterBeløp(totaler.totalt_inkl_mva)}</strong></span>
                </div>
              </div>

              <div className="steg-navigasjon">
                <button className="btn btn-secondary" onClick={onClose}>
                  Avbryt
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setSteg(2)}
                  disabled={!valgtSakId || !fakturanummer || !timesats || !antallTimer}
                >
                  Neste: Fordeling →
                </button>
              </div>
            </div>
          )}

          {/* STEG 2: Fakturafordeling */}
          {steg === 2 && (
            <div className="steg-innhold">
              <h3>Fakturafordeling</h3>

              {/* Vis rettshjelpsvilkår hvis tilgjengelig */}
              {lasterVilkår && (
                <div style={{
                  background: '#f0f4f8',
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  ⏳ Laster rettshjelpsvilkår...
                </div>
              )}

              {rettshjelpsvilkår && (
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px' }}>
                    🛡️ Registrerte rettshjelpsvilkår for saken
                  </strong>

                  {rettshjelpsvilkår.bkf && (
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>BKF-forsikring:</div>
                      <div style={{ fontSize: '13px', opacity: 0.95 }}>
                        • Selskap: {rettshjelpsvilkår.bkf.selskap_navn}
                        {rettshjelpsvilkår.bkf.polisenummer && ` (Police: ${rettshjelpsvilkår.bkf.polisenummer})`}
                      </div>
                      {rettshjelpsvilkår.bkf.timesats && (
                        <div style={{ fontSize: '13px', opacity: 0.95 }}>
                          • Timesats: {rettshjelpsvilkår.bkf.timesats} kr
                          {rettshjelpsvilkår.bkf.tillegg > 0 && ` + ${rettshjelpsvilkår.bkf.tillegg} kr tillegg`}
                        </div>
                      )}
                      {rettshjelpsvilkår.bkf.maks_belop && (
                        <div style={{ fontSize: '13px', opacity: 0.95 }}>
                          • Maksbeløp: {rettshjelpsvilkår.bkf.maks_belop.toLocaleString('nb-NO')} kr
                        </div>
                      )}
                    </div>
                  )}

                  {rettshjelpsvilkår.innbo && (
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>Innboforsikring:</div>
                      <div style={{ fontSize: '13px', opacity: 0.95 }}>
                        • Selskap: {rettshjelpsvilkår.innbo.selskap_navn}
                        {rettshjelpsvilkår.innbo.polisenummer && ` (Police: ${rettshjelpsvilkår.innbo.polisenummer})`}
                      </div>
                      {rettshjelpsvilkår.innbo.dekningsgrad && (
                        <div style={{ fontSize: '13px', opacity: 0.95 }}>
                          • Dekningsgrad: {(rettshjelpsvilkår.innbo.dekningsgrad * 100)}%
                        </div>
                      )}
                      {rettshjelpsvilkår.innbo.maks_belop && (
                        <div style={{ fontSize: '13px', opacity: 0.95 }}>
                          • Maksbeløp: {rettshjelpsvilkår.innbo.maks_belop.toLocaleString('nb-NO')} kr
                        </div>
                      )}
                      {rettshjelpsvilkår.innbo.aktiveringsgebyr && (
                        <div style={{ fontSize: '13px', opacity: 0.95 }}>
                          • Aktiveringsgebyr: {rettshjelpsvilkår.innbo.aktiveringsgebyr.toLocaleString('nb-NO')} kr
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>BKF-selskap</label>
                <select
                  value={bkfSelskapId}
                  onChange={(e) => setBkfSelskapId(e.target.value)}
                >
                  <option value="">-- Velg BKF-selskap --</option>
                  {forsikringsselskap
                    .filter(s => s.type === 'boligkjoper')
                    .map(s => (
                      <option key={s.id} value={s.id}>
                        {s.navn}
                        {s.bkf_timesats_type === 'offentlig_pluss' &&
                          ` (Offentlig sats + ${s.bkf_tillegg || 0} kr)`}
                      </option>
                    ))}
                </select>
                {leverandorNavn && !bkfSelskapId && (
                  <div style={{ marginTop: '8px' }}>
                    <small style={{ color: '#666' }}>
                      Funnet på faktura: <strong>{leverandorNavn}</strong>
                    </small>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => alert('Opprett nytt selskap-funksjonalitet kommer snart!')}
                      style={{ display: 'block', marginTop: '4px', padding: '4px 8px', fontSize: '14px' }}
                    >
                      + Opprett nytt selskap: "{leverandorNavn}"
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Innboforsikring-selskap</label>
                <select
                  value={innboSelskapId}
                  onChange={(e) => setInnboSelskapId(e.target.value)}
                >
                  <option value="">-- Velg innboforsikring --</option>
                  {forsikringsselskap
                    .filter(s => s.type === 'innbo')
                    .map(s => (
                      <option key={s.id} value={s.id}>
                        {s.navn}
                        {s.standard_dekningsgrad &&
                          ` (${(s.standard_dekningsgrad * 100)}% dekning)`}
                      </option>
                    ))}
                </select>
              </div>

              {innboSelskapId && (
                <>
                  <div className="form-group">
                    <label>Polisenummer</label>
                    <input
                      type="text"
                      value={innboPolisenummer}
                      onChange={(e) => setInnboPolisenummer(e.target.value)}
                      placeholder="12345678"
                    />
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={egenandel.betaltDirekte}
                        onChange={(e) => setEgenandel({
                          ...egenandel,
                          betaltDirekte: e.target.checked
                        })}
                      />
                      <span>Egenandel er allerede betalt direkte til forsikringsselskap</span>
                    </label>
                    <small className="form-hint">
                      Hvis klienten har betalt egenandel direkte til forsikringsselskapet,
                      vil den kun trekkes fra forsikringsandelen (ikke legges til klientandel).
                    </small>
                  </div>
                </>
              )}

              <div className="steg-navigasjon">
                <button className="btn btn-secondary" onClick={() => setSteg(1)}>
                  ← Tilbake
                </button>
                <button
                  className="btn btn-primary"
                  onClick={opprettFaktura}
                  disabled={loading || !bkfSelskapId || !innboSelskapId}
                >
                  {loading ? 'Beregner...' : 'Beregn og lagre'}
                </button>
              </div>
            </div>
          )}

          {/* STEG 3: Oppsummering */}
          {steg === 3 && fordeling && (
            <div className="steg-innhold">
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>Faktura opprettet!</h3>
                <p>Fakturafordeling er beregnet og lagret.</p>
              </div>

              <div className="fordeling-oppsummering">
                <h4>Fordeling</h4>

                <div className="fordeling-rad bkf">
                  <div className="fordeling-ikon">🏦</div>
                  <div className="fordeling-info">
                    <div className="fordeling-navn">
                      {bkfSelskap?.navn || 'BKF'}
                    </div>
                    <div className="fordeling-detaljer">
                      {fordeling.bkf_timer} timer × {formaterBeløp(fordeling.bkf_timesats)}/t
                    </div>
                  </div>
                  <div className="fordeling-beløp">
                    {formaterBeløp(fordeling.bkf_totalt)}
                  </div>
                </div>

                <div className="fordeling-rad innbo">
                  <div className="fordeling-ikon">🏠</div>
                  <div className="fordeling-info">
                    <div className="fordeling-navn">
                      {innboSelskap?.navn || 'Innbo'} ({(fordeling.innbo_dekningsgrad * 100)}%)
                    </div>
                    {fordeling.innbo_egenandel_trukket > 0 && (
                      <div className="fordeling-detaljer">
                        Egenandel: -{formaterBeløp(fordeling.innbo_egenandel_trukket)}
                      </div>
                    )}
                  </div>
                  <div className="fordeling-beløp">
                    {formaterBeløp(fordeling.innbo_totalt)}
                  </div>
                </div>

                <div className="fordeling-rad klient">
                  <div className="fordeling-ikon">👤</div>
                  <div className="fordeling-info">
                    <div className="fordeling-navn">
                      Klient
                    </div>
                    {fordeling.klient_egenandel_lagt_til > 0 && (
                      <div className="fordeling-detaljer">
                        Ink. egenandel: +{formaterBeløp(fordeling.klient_egenandel_lagt_til)}
                      </div>
                    )}
                  </div>
                  <div className="fordeling-beløp">
                    {formaterBeløp(fordeling.klient_totalt)}
                  </div>
                </div>

                <div className="fordeling-total">
                  <strong>Sum:</strong>
                  <strong>
                    {formaterBeløp(
                      fordeling.bkf_totalt +
                      fordeling.innbo_totalt +
                      fordeling.klient_totalt
                    )}
                  </strong>
                </div>
              </div>

              <div className="steg-navigasjon">
                <button className="btn btn-primary" onClick={onClose}>
                  Lukk
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
